// src/pages/user/RegisterPage.js

import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import MainNavbar from '../../components/MainNavbar';
import { auth, googleProvider, isBotCheckAvailable } from '../../firebaseConfig';
import { createUserWithEmailAndPassword, signInWithPopup, sendEmailVerification } from 'firebase/auth';
import CustomGoogleButton from '../../components/common/buttons/CustomGoogleButton';
import { registerUser, RegistrationError } from '../../firebase/user';
import FormField from '../../components/common/FormField';
import Alert from '../../components/common/Alert';
import ActionButton from '../../components/common/ActionButton';
import { useAuth } from '../../firebase/useAuth';

function RegisterPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { appCheckBlocked } = useAuth();

  const handleAuth = async (createAuthSession) => {
    setIsLoading(true);
    setErrorMessage('');
    setSuccessMessage('');
    try {
      // Preflight before creating the Auth account: without an App Check
      // token every Firestore request is denied, so proceeding would only
      // create an Auth account with no profile (an orphan).
      if (!(await isBotCheckAvailable())) {
        setErrorMessage('A VPN or privacy tool is likely blocking our bot check (reCAPTCHA). Allow it for this site or try another network, then try again.');
        return;
      }

      const result = await createAuthSession();
      const user = result.user;

      await registerUser(user.uid, user.email);

      const isEmailSignUp = user.providerData.some((provider) => provider.providerId === 'password');

      if (isEmailSignUp) {
        await sendEmailVerification(user);
      }

      setSuccessMessage('Registration successful! Redirecting to your dashboard...');

      setTimeout(() => {
        navigate('/dashboard');
      }, 1500);
    } catch (error) {
      let customErrorMessage = 'An unexpected error occurred. Please try again.';
      if (error instanceof RegistrationError) {
        console.error('Profile initialization failed:', error);
        customErrorMessage = appCheckBlocked
          ? 'Your account was created, but a VPN or privacy tool is likely blocking our bot check (reCAPTCHA). Allow it for this site, then refresh.'
          : 'Your account was created, but your profile could not be initialized. Please refresh the page — if the issue persists, contact support.';
      } else {
        switch (error.code) {
          case 'auth/weak-password':
            customErrorMessage = 'Password is too weak. It should be at least 6 characters long.';
            break;
          case 'auth/email-already-in-use':
            customErrorMessage = 'This email address is already registered. Please try logging in.';
            break;
          case 'auth/invalid-email':
            customErrorMessage = 'The email address is not valid.';
            break;
          case 'auth/popup-closed-by-user':
            customErrorMessage = 'The sign-up popup was closed. Please try again.';
            break;
          case 'auth/account-exists-with-different-credential':
            customErrorMessage = 'An account already exists with this email using a different sign-in method.';
            break;
          default:
            console.error('Authentication Error:', error);
            break;
        }
      }
      setErrorMessage(customErrorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      setErrorMessage('Passwords do not match!');
      return;
    }
    handleAuth(() => createUserWithEmailAndPassword(auth, email, password));
  };

  const handleGoogleSignUp = () => {
    handleAuth(() => signInWithPopup(auth, googleProvider));
  };

  return (
    <div className="flex flex-col min-h-screen bg-background-primary">
      <MainNavbar />

      {/* Main Content Container with Enhanced Background */}
      <div className="flex-grow flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        {/* Background Elements - More Bubbles */}
        <div className="absolute inset-0 opacity-20 dark:opacity-12 pointer-events-none">
          {/* Main Bubbles */}
          <div
            className="absolute top-1/4 left-1/4 w-32 h-32 rounded-full animate-pulse"
            style={{
              background: `linear-gradient(135deg, var(--color-primary), var(--color-secondary))`,
              filter: 'blur(40px)',
              animation: 'float 6s ease-in-out infinite',
            }}
          />
          <div
            className="absolute bottom-1/3 right-1/4 w-24 h-24 rounded-full animate-pulse"
            style={{
              background: `linear-gradient(45deg, var(--color-accent), var(--color-primary))`,
              filter: 'blur(30px)',
              animation: 'float 8s ease-in-out infinite reverse',
              animationDelay: '1s',
            }}
          />
          <div
            className="absolute top-1/2 right-1/3 w-20 h-20 rounded-full animate-pulse"
            style={{
              background: `linear-gradient(225deg, var(--color-secondary), var(--color-accent))`,
              filter: 'blur(25px)',
              animation: 'float 10s ease-in-out infinite',
              animationDelay: '2s',
            }}
          />

          {/* Additional Bubbles */}
          <div
            className="absolute top-1/6 left-1/2 w-28 h-28 rounded-full animate-pulse"
            style={{
              background: `linear-gradient(90deg, var(--color-primary), var(--color-accent))`,
              filter: 'blur(35px)',
              animation: 'float 7s ease-in-out infinite',
              animationDelay: '0.5s',
            }}
          />
          <div
            className="absolute bottom-1/6 left-1/5 w-22 h-22 rounded-full animate-pulse"
            style={{
              background: `linear-gradient(180deg, var(--color-secondary), var(--color-primary))`,
              filter: 'blur(28px)',
              animation: 'float 9s ease-in-out infinite reverse',
              animationDelay: '1.5s',
            }}
          />
          <div
            className="absolute top-2/3 right-1/6 w-18 h-18 rounded-full animate-pulse"
            style={{
              background: `linear-gradient(270deg, var(--color-accent), var(--color-secondary))`,
              filter: 'blur(22px)',
              animation: 'float 11s ease-in-out infinite',
              animationDelay: '2.5s',
            }}
          />
          <div
            className="absolute top-1/8 right-1/2 w-16 h-16 rounded-full animate-pulse"
            style={{
              background: `linear-gradient(315deg, var(--color-primary), var(--color-secondary))`,
              filter: 'blur(20px)',
              animation: 'float 5s ease-in-out infinite reverse',
              animationDelay: '3s',
            }}
          />
          <div
            className="absolute bottom-1/8 right-1/8 w-14 h-14 rounded-full animate-pulse"
            style={{
              background: `linear-gradient(45deg, var(--color-secondary), var(--color-accent))`,
              filter: 'blur(18px)',
              animation: 'float 8.5s ease-in-out infinite',
              animationDelay: '0.8s',
            }}
          />
        </div>

        <div className="max-w-md w-full space-y-8 relative z-10">
          {/* Header Section */}
          <div className="text-center animate-fade-in">
            <h2 className="text-4xl font-black text-content-primary leading-tight drop-shadow-sm">Create your account</h2>
            <p className="mt-3 text-lg text-content-secondary font-medium opacity-90">
              Or{' '}
              <Link to="/user/login" className="font-bold text-primary hover:text-opacity-80 transition-all duration-200 hover:drop-shadow-sm">
                sign in to your existing account
              </Link>
            </p>
          </div>

          {/* Main Form Card */}
          <div
            className="relative rounded-2xl backdrop-blur-sm border-2 border-border overflow-hidden group animate-fade-in"
            style={{
              background: `linear-gradient(135deg, var(--color-background-secondary), var(--color-background-secondary))`,
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
              animationDelay: '150ms',
            }}
          >
            {/* Enhanced glassmorphic overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-white/30 to-transparent opacity-70 dark:from-white/10 dark:opacity-50 pointer-events-none" />

            {/* Audio bars in TOP-RIGHT and BOTTOM-LEFT corners only - MOVED UP */}
            <div className="absolute inset-0 opacity-25 dark:opacity-20 pointer-events-none">
              {/* Top-right corner */}
              <div className="absolute top-4 right-4 flex gap-1 group-hover:scale-110 transition-transform duration-700">
                {[...Array(6)].map((_, i) => (
                  <div
                    key={i}
                    className="rounded-full"
                    style={{
                      width: '3px',
                      height: `${12 + i * 3}px`,
                      background: `linear-gradient(180deg, var(--color-primary), var(--color-secondary))`,
                      animation: `audio-bar ${3 + i * 0.45}s ease-in-out infinite alternate`,
                      animationDelay: `${i * 0.3}s`,
                    }}
                  />
                ))}
              </div>

              {/* Bottom-left corner - MOVED UP */}
              <div className="absolute bottom-2 left-4 flex gap-1 group-hover:scale-110 transition-transform duration-700">
                {[...Array(3)].map((_, i) => (
                  <div
                    key={i}
                    className="rounded-full"
                    style={{
                      width: '2px',
                      height: `${8 + i * 2}px`,
                      background: `linear-gradient(180deg, var(--color-accent), var(--color-primary))`,
                      animation: `audio-bar ${3 + i * 0.45}s ease-in-out infinite alternate`,
                      animationDelay: `${i * 0.3}s`,
                    }}
                  />
                ))}
              </div>
            </div>

            {/* Form Content */}
            <div className="relative z-10 p-8">
              {/* Alert Messages */}
              {errorMessage && (
                <div className="mb-6 animate-slide-in-center">
                  <Alert message={errorMessage} type="error" />
                </div>
              )}
              {successMessage && (
                <div className="mb-6 animate-slide-in-center">
                  <Alert message={successMessage} type="success" />
                </div>
              )}

              {/* Registration Form */}
              <form className="space-y-6" onSubmit={handleRegister}>
                <FormField id="email" label="Email address" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />

                <FormField
                  id="password"
                  label="Password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  helpText="Must be at least 6 characters long."
                  required
                />

                <FormField
                  id="confirm-password"
                  label="Confirm Password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />

                {/* Submit Button */}
                <div className="pt-2">
                  <ActionButton
                    type="submit"
                    loading={isLoading}
                    className="w-full justify-center py-3 px-6 text-base font-bold text-white transition-all duration-200 rounded-xl bg-primary"
                  >
                    Create an account
                  </ActionButton>
                </div>
              </form>

              {/* Divider */}
              <div className="mt-8">
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-border opacity-60" />
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span
                      className="px-4 text-content-secondary font-semibold uppercase tracking-wide"
                      style={{
                        background: `linear-gradient(135deg, var(--color-background-secondary), var(--color-background-secondary))`,
                      }}
                    >
                      OR
                    </span>
                  </div>
                </div>
              </div>

              {/* Google Sign Up Button */}
              <div className="mt-8">
                <div className="relative">
                  <CustomGoogleButton
                    style={{
                      width: '100%',
                      borderRadius: '0.75rem',
                      height: '48px',
                      fontSize: '16px',
                      fontWeight: '600',
                    }}
                    label="Sign Up with Google"
                    onClick={handleGoogleSignUp}
                    disabled={isLoading}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Terms and Privacy */}
          <div className="text-center animate-fade-in" style={{ animationDelay: '300ms' }}>
            <p className="text-xs text-content-secondary opacity-80 leading-relaxed">
              By creating an account, you agree to our{' '}
              <Link
                to="/terms-of-service"
                className="font-semibold text-primary hover:text-opacity-80 transition-colors duration-200 underline decoration-primary/30 hover:decoration-primary/60"
              >
                Terms of Service
              </Link>{' '}
              and{' '}
              <Link
                to="/privacy-policy"
                className="font-semibold text-primary hover:text-opacity-80 transition-colors duration-200 underline decoration-primary/30 hover:decoration-primary/60"
              >
                Privacy Policy
              </Link>
              .
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default RegisterPage;